<template>
  <div class="space-y-1.5">
    <div v-for="(step, index) in pipeline" :key="index"
      class="flex items-center gap-2 p-2 bg-slate-800/40 rounded flex-wrap">
      <div class="flex flex-col gap-0.5 shrink-0">
        <button type="button" class="text-gray-400 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed leading-none"
          :disabled="index === 0" title="Move up" @click="moveStep(index, -1)">
          <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M10 5l-6 7h12z" /></svg>
        </button>
        <button type="button" class="text-gray-400 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed leading-none"
          :disabled="index === pipeline.length - 1" title="Move down" @click="moveStep(index, 1)">
          <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M10 15l6-7H4z" /></svg>
        </button>
      </div>

      <span v-if="issuesForStep(index).length" class="shrink-0 cursor-help"
        :title="issuesForStep(index).map(i => i.message).join('\n')">⚠</span>

      <BuilderSelect class="text-sm px-1 py-1 shrink-0" v-model="step.type" @change="onTypeChange(step)">
        <option v-for="(meta, type) in TRANSFORM_TYPES" :key="type" :value="type">{{ meta.label }}</option>
      </BuilderSelect>

      <div v-for="paramDef in TRANSFORM_TYPES[step.type]?.params ?? []" :key="paramDef.key"
        class="flex items-center gap-1 shrink-0">
        <span class="text-xs text-gray-400">{{ paramDef.label }}</span>
        <BuilderInput type="text" class="w-16 text-sm px-1 py-1" v-model="step.params[paramDef.key]" />
      </div>

      <button type="button" class="text-red-400 hover:text-red-300 shrink-0 ml-auto" title="Remove transform"
        @click="removeStep(index)">
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <button type="button" class="text-xs text-cyan-400 hover:text-cyan-300" @click="addStep">
      + Add transform
    </button>
  </div>
</template>

<script setup>
/**
 * TransformEditor.vue
 *
 * Editor for an ordered value-transform pipeline (see valueTransforms.js)
 * — editor-only UI, design-time, strategy-agnostic. Operates directly on
 * `pipeline`, which is `binding.transforms` (part of the persisted
 * AutomationBinding model, not BindingEditorState — see "Architecture
 * Correction — Move Value Transforms into AutomationBinding"); this
 * component just mutates whatever array it's handed.
 * `issues` (from validateTransformPipeline.js, sliced per step by the
 * parent) is display-only here, mirroring MappingRuleRow.vue's own
 * per-row issue badge.
 *
 * Uses BuilderSelect/BuilderInput, not the global form-select/form-input
 * — those are styled for the light property-panel forms; see
 * BuilderSelect.vue's doc comment.
 */
import { computed } from 'vue';
import { TRANSFORM_TYPES, defaultParamsFor } from '../../../automation/schema/valueTransforms.js';
import { validateTransformPipeline } from '../../../automation/schema/validateTransformPipeline.js';
import BuilderSelect from './BuilderSelect.vue';
import BuilderInput from './BuilderInput.vue';

const props = defineProps({
  pipeline: { type: Array, required: true }, // TransformStep[] — mutated directly
});

const issues = computed(() => validateTransformPipeline(props.pipeline));

function issuesForStep(index) {
  return issues.value.filter(issue => issue.stepIndex === index);
}

function addStep() {
  const type = 'float';
  props.pipeline.push({ type, params: defaultParamsFor(type) });
}

function removeStep(index) {
  props.pipeline.splice(index, 1);
}

function moveStep(index, delta) {
  const target = index + delta;
  [props.pipeline[index], props.pipeline[target]] = [props.pipeline[target], props.pipeline[index]];
}

/** Resets a step's params when its type changes — old params won't match the new type's keys. */
function onTypeChange(step) {
  step.params = defaultParamsFor(step.type);
}
</script>
